import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
  query,
  orderBy,
} from "firebase/firestore";
import {
  Trash2,
  Pencil,
  Power,
  PowerOff,
  X,
  CalendarDays,
  Link as LinkIcon,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

import InnovationHubHeader from "../components/InnovationHubHeader";
import { db } from "../web/src/firebaseClient";
import InnovationHubFooter from "../components/InnovationHubFooter";

const COLLECTION_NAME = "announcements";

export default function AdminAnnouncementManagement() {
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkAdmin();
    fetchAnnouncements();
  }, []);

  const checkAdmin = async () => {
    const user = getAuth().currentUser;

    if (!user) {
      setIsAdmin(false);
      return;
    }

    const token = await user.getIdTokenResult();
    setIsAdmin(token.claims.role === "admin");
  };

  const fetchAnnouncements = async () => {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(q);

    const data = snapshot.docs
      .map((docItem, index) => ({
        id: docItem.id,
        fallbackOrder: index + 1,
        ...docItem.data(),
      }))
      .sort((a, b) => {
        const orderA =
          typeof a.order === "number" ? a.order : a.fallbackOrder;
        const orderB =
          typeof b.order === "number" ? b.order : b.fallbackOrder;

        return orderA - orderB;
      });

    setAnnouncements(data);
  };

  const isExternalUrl = (url) => {
    const cleanUrl = url?.trim() || "";
    return cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://");
  };

  const toDateInputValue = (dateValue) => {
    if (!dateValue) return "";

    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    const localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    );

    return localDate.toISOString().slice(0, 16);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "Belirtilmedi";

    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

    return date.toLocaleString("tr-TR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setTitle(item.title || "");
    setDescription(item.description || "");
    setTargetUrl(item.targetUrl || "");
    setStartDate(toDateInputValue(item.startDate));
    setEndDate(toDateInputValue(item.endDate));
    setIsActive(Boolean(item.isActive));

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingItem(null);
    setTitle("");
    setDescription("");
    setTargetUrl("");
    setStartDate("");
    setEndDate("");
    setIsActive(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert("Lütfen duyuru başlığı girin.");
      return;
    }

    if (!description.trim()) {
      alert("Lütfen duyuru açıklaması girin.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        targetUrl: targetUrl.trim(),
        isActive,
        startDate: startDate ? Timestamp.fromDate(new Date(startDate)) : null,
        endDate: endDate ? Timestamp.fromDate(new Date(endDate)) : null,
        updatedAt: serverTimestamp(),
      };

      if (editingItem) {
        await updateDoc(doc(db, COLLECTION_NAME, editingItem.id), payload);
      } else {
        await addDoc(collection(db, COLLECTION_NAME), {
          ...payload,
          order: announcements.length + 1,
          createdAt: serverTimestamp(),
        });
      }

      resetForm();
      await fetchAnnouncements();
    } catch (error) {
      console.error(error);
      alert("Duyuru kaydedilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (item) => {
    await updateDoc(doc(db, COLLECTION_NAME, item.id), {
      isActive: !item.isActive,
      updatedAt: serverTimestamp(),
    });

    await fetchAnnouncements();
  };

  const moveAnnouncement = async (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= announcements.length) return;

    const currentItem = announcements[index];
    const targetItem = announcements[newIndex];

    const currentOrder =
      typeof currentItem.order === "number"
        ? currentItem.order
        : currentItem.fallbackOrder || index + 1;

    const targetOrder =
      typeof targetItem.order === "number"
        ? targetItem.order
        : targetItem.fallbackOrder || newIndex + 1;

    try {
      await updateDoc(doc(db, COLLECTION_NAME, currentItem.id), {
        order: targetOrder,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, COLLECTION_NAME, targetItem.id), {
        order: currentOrder,
        updatedAt: serverTimestamp(),
      });

      await fetchAnnouncements();
    } catch (error) {
      console.error(error);
      alert("Duyuru sırası değiştirilirken bir hata oluştu.");
    }
  };

  const deleteItem = async (item) => {
    const confirmDelete = window.confirm("Bu duyuruyu silmek istiyor musunuz?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, COLLECTION_NAME, item.id));

      if (editingItem?.id === item.id) {
        resetForm();
      }

      await fetchAnnouncements();
    } catch (error) {
      console.error(error);
      alert("Duyuru silinirken bir hata oluştu.");
    }
  };

  if (isAdmin === null) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-6">Yükleniyor...</div>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-10 text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Bu sayfaya erişim yetkiniz yok
          </h2>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#eef5ff]">
      <InnovationHubHeader />

      <section className="p-6">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="mb-4 text-sm font-medium text-[#1565C0] hover:underline"
            >
              ← Admin Panel
            </button>

            <h1 className="text-2xl font-semibold text-[#173b7a]">
              Duyuru Yönetimi
            </h1>

            <p className="mt-2 text-sm text-gray-500">
              Ana sayfada veya duyuru alanlarında gösterilecek duyuruları
              buradan yönetebilirsiniz.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-[#173b7a]">
                {editingItem ? "Duyuru Güncelle" : "Yeni Duyuru Ekle"}
              </h2>

              {editingItem && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-200"
                >
                  <X size={16} />
                  Vazgeç
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Başlık
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Örn: Yeni dönem başvuruları başladı"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Açıklama
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Duyuru açıklamasını yazın..."
                  rows={5}
                  className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Link URL{" "}
                  <span className="font-normal text-gray-400">
                    (opsiyonel)
                  </span>
                </label>
                <input
                  type="text"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  placeholder="/itk-starter veya https://..."
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Bitiş Tarihi
                  </label>
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-[#f8fbff] p-4">
                <div>
                  <p className="text-sm font-medium text-[#173b7a]">
                    Yayın Durumu
                  </p>
                  <p className="text-xs text-gray-500">
                    Aktif seçilirse tarih aralığına göre yayında görünür.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setIsActive((prev) => !prev)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {isActive ? "Aktif" : "Pasif"}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-[#1565C0] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0d4f9c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading
                  ? "Kaydediliyor..."
                  : editingItem
                  ? "Duyuruyu Güncelle"
                  : "Duyuruyu Kaydet"}
              </button>
            </form>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <h2 className="mb-5 text-lg font-semibold text-[#173b7a]">
              Mevcut Duyurular
            </h2>

            {announcements.length === 0 ? (
              <div className="rounded-xl bg-[#f8fbff] p-6 text-center text-sm text-gray-500">
                Henüz duyuru eklenmemiş.
              </div>
            ) : (
              <div className="space-y-4">
                {announcements.map((item, index) => {
                  const cleanTargetUrl = item.targetUrl?.trim() || "";

                  return (
                    <div
                      key={item.id}
                      className={`grid grid-cols-1 gap-4 rounded-2xl border p-4 md:grid-cols-[1fr_auto] ${
                        editingItem?.id === item.id
                          ? "border-[#1565C0] bg-blue-50/60"
                          : "border-gray-100 bg-[#f8fbff]"
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-[#173b7a]">
                            {item.title || "Başlıksız Duyuru"}
                          </h3>

                          <span
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              item.isActive
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {item.isActive ? "Aktif" : "Pasif"}
                          </span>
                        </div>

                        <p className="text-sm leading-relaxed text-gray-600">
                          {item.description}
                        </p>

                        {cleanTargetUrl && (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <LinkIcon size={15} />

                            {isExternalUrl(cleanTargetUrl) ? (
                              <a
                                href={cleanTargetUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="break-all hover:text-[#1565C0] hover:underline"
                              >
                                {cleanTargetUrl}
                              </a>
                            ) : (
                              <span className="break-all text-[#1565C0]">
                                {cleanTargetUrl}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-1 gap-2 text-xs text-gray-500 md:grid-cols-2">
                          <div className="flex items-center gap-2">
                            <CalendarDays size={15} />
                            <span>Başlangıç: {formatDate(item.startDate)}</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <CalendarDays size={15} />
                            <span>Bitiş: {formatDate(item.endDate)}</span>
                          </div>
                        </div>

                        <p className="text-xs text-gray-400">
                          Sıra:{" "}
                          {typeof item.order === "number"
                            ? item.order
                            : item.fallbackOrder}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 md:flex-col">
                        <button
                          type="button"
                          onClick={() => moveAnnouncement(index, "up")}
                          disabled={index === 0}
                          className="rounded-lg bg-white p-2 text-gray-600 shadow-sm transition hover:text-[#1565C0] disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <ArrowUp size={18} />
                        </button>

                        <button
                          type="button"
                          onClick={() => moveAnnouncement(index, "down")}
                          disabled={index === announcements.length - 1}
                          className="rounded-lg bg-white p-2 text-gray-600 shadow-sm transition hover:text-[#1565C0] disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <ArrowDown size={18} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="rounded-lg bg-white p-2 text-[#1565C0] shadow-sm transition hover:bg-blue-50"
                        >
                          <Pencil size={18} />
                        </button>

                        <button
                          type="button"
                          onClick={() => toggleActive(item)}
                          className="rounded-lg bg-white p-2 shadow-sm transition hover:text-[#1565C0]"
                        >
                          {item.isActive ? (
                            <PowerOff size={18} />
                          ) : (
                            <Power size={18} />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteItem(item)}
                          className="rounded-lg bg-white p-2 text-red-500 shadow-sm transition hover:bg-red-50"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}